/* eslint-disable no-underscore-dangle */
import React, { useState } from "react";
import PropTypes from "prop-types";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Fab from "@material-ui/core/Fab";
import DownloadIcon from "@material-ui/icons/GetApp";
import Button from "@material-ui/core/Button";
import { Link as GatsbyLink } from "gatsby";
import SearchIcon from "@material-ui/icons/Search";
import Zoom from "@material-ui/core/Zoom";
import { format } from "date-fns";
import PortableText from "../PortableText";
import { mediaType, themeType } from "../../types";
import Section from "../Section/Section";
import { getHeaderText, sortAlphabetically } from "../../lib/helpers";
import MediaGrid from "../MediaGrid";
import NoContent from "../NoContent";
import InlineList from "../InlineList";
import MediaViewer from "../MediaViewer";
import DetailSection from "../DetailSection";
import ThemeLink from "../ThemeLink";
import CategoryLink from "../CategoryLink";
import useDownloadMedia from "../../hooks/useDownloadMedia";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
  },
  media: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  meta: {
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(2),
    transitionProperty: "transform",
    transitionDuration: theme.transitions.duration.standard,
    transitionTimingFunction: theme.transitions.easing.easeInOut,
    [theme.breakpoints.up("md")]: {
      transform: `translateY(-${theme.spacing(14)}px)`,
      maxWidth: "75%",
    },
  },
  metaIsGrounded: {
    transform: "translateY(0)",
  },
  section: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  relatedMedia: {
    marginBottom: theme.spacing(4),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  fabIcon: {
    marginRight: theme.spacing(1),
  },
}));

function RelatedMediaGroup({ media, theme }) {
  if (media.length === 0 || media[0].themes.length === 0) {
    return null;
  }

  const classes = useStyles();
  return (
    <div className={classes.relatedMedia}>
      <Typography variant="h6">
        Related to{" "}
        <Link
          component={GatsbyLink}
          to="/media#mediaSearch"
          state={{ filter: { themes: [theme] } }}
        >
          {theme.title}
        </Link>
      </Typography>
      <MediaGrid media={media.length > 2 ? media.slice(0, 3) : media} />
    </div>
  );
}

function MediaDetail({ media, relatedMedia }) {
  const relatedMediaGroups =
    relatedMedia.length > 0
      ? media.themes.map((theme) => {
          const mediaByTheme = relatedMedia.filter((relatedMediaItem) =>
            relatedMediaItem.themes
              .map((relatedMediaTheme) => relatedMediaTheme.id)
              .includes(theme.id)
          );

          return { theme, media: mediaByTheme };
        })
      : [];

  const [mediaIsHovered, setMediaIsHovered] = useState(false);
  const [mediaIsPlaying, setMediaIsPlaying] = useState(false);

  const theme = useTheme();
  const isSmallOrDownViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();
  const [title, source] = getHeaderText(media.title);
  const hasMetadata =
    media._rawBackstory ||
    media._rawInview ||
    media.themes.length > 0 ||
    media.category;

  const themeLinks = [...media.themes]
    .sort(sortAlphabetically)
    .map((t) => <ThemeLink theme={t} />);

  const { isDownloadable, url } = useDownloadMedia(media);

  return (
    <div className={classes.root}>
      <Section isArticle background="dark" heading={title} subheading={source}>
        <MediaViewer
          media={media}
          onEnter={() => {
            setMediaIsHovered(true);
          }}
          onLeave={() => {
            setMediaIsHovered(false);
          }}
          onPlaying={() => {
            setMediaIsPlaying(true);
          }}
          onPause={() => {
            setMediaIsPlaying(false);
          }}
        />

        {hasMetadata && (
          <Paper
            elevation={
              isSmallOrDownViewport || mediaIsPlaying || mediaIsHovered ? 0 : 4
            }
            className={`${classes.meta} ${
              !isSmallOrDownViewport && (mediaIsPlaying || mediaIsHovered)
                ? classes.metaIsGrounded
                : ""
            }`}
          >
            {media._rawBackstory && media._rawBackstory.length > 0 && (
              <DetailSection title="Backstory">
                <PortableText blocks={media._rawBackstory} />
              </DetailSection>
            )}

            {media._rawInview && media._rawInview.length > 0 && (
              <DetailSection title="InView">
                <PortableText blocks={media._rawInview} />
              </DetailSection>
            )}

            {media.themes.length > 0 && (
              <DetailSection title="Themes">
                <InlineList items={themeLinks} />
              </DetailSection>
            )}

            {media.category && (
              <DetailSection title="Category">
                <CategoryLink category={media.category} />
              </DetailSection>
            )}

            <DetailSection title="Published">
              <Typography variant="body1">
                {format(media.publishedAt, "MMMM Do, YYYY")}
              </Typography>
            </DetailSection>
          </Paper>
        )}
      </Section>
      <Section heading="Related Media">
        {relatedMediaGroups.length > 0 ? (
          relatedMediaGroups.map((group) => (
            <RelatedMediaGroup
              key={group.theme.id}
              theme={group.theme}
              media={group.media}
            />
          ))
        ) : (
          <NoContent text="No Related Media">
            <Button
              to="/media#mediaSearch"
              component={GatsbyLink}
              color="primary"
              variant="contained"
              startIcon={<SearchIcon />}
            >
              Search Media
            </Button>
          </NoContent>
        )}
      </Section>
      {isDownloadable && (
        <Zoom in>
          <Fab
            component="a"
            title="download media"
            variant="extended"
            className={classes.fab}
            color="primary"
            aria-label="download"
            href={url}
            download
          >
            <DownloadIcon className={classes.fabIcon} />
            Download
          </Fab>
        </Zoom>
      )}
    </div>
  );
}

MediaDetail.defaultProps = {
  relatedMedia: [],
};

MediaDetail.propTypes = {
  media: mediaType.isRequired,
  relatedMedia: PropTypes.arrayOf(mediaType),
};

RelatedMediaGroup.defaultProps = {
  media: [],
};

RelatedMediaGroup.propTypes = {
  media: PropTypes.arrayOf(mediaType),
  theme: themeType.isRequired,
};

export default MediaDetail;
